import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { MILESTONE_DOMAINS, type MilestoneDomain } from '../lib/milestoneDomains';

/** One milestone type (election_milestone_types / legislative_milestone_types) as the API serves it. */
export interface MilestoneType {
  value: string;
  label: string;
  description: string | null;
  category: string;
  citizen_visible: boolean;
  notify_by_default: boolean;
  citizen_reminder_lead_days: number[];
  reminder_message: string | null;
  reminder_link_url: string | null;
  default_sort_order: number;
  is_system: boolean;
  active: boolean;
  usage_count: number;
  upcoming_count: number;
  upcoming_notifying_count: number;
}

/**
 * The milestone type vocabulary for a domain (Documentation/ELECTION_MILESTONE_TYPES_PRD.md;
 * legislative: ELECTION_OUTCOMES_PRD.md §5.0). Admin-managed, so it comes from the
 * API — never a frontend constant. `includeRetired` adds retired types (the
 * Milestone Types tab lists them; the Add Milestone dropdown does not).
 */
export function useMilestoneTypes({
  includeRetired = false,
  domain = 'election',
}: { includeRetired?: boolean; domain?: MilestoneDomain } = {}) {
  const config = MILESTONE_DOMAINS[domain];
  return useQuery({
    queryKey: [...config.typesQueryKey, includeRetired],
    queryFn: async () => {
      const response = await api.get(config.typesPath, {
        params: includeRetired ? { include_retired: 'true' } : undefined,
      });
      return (response.data.data ?? []) as MilestoneType[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
